// Small helper to set common SEO meta tags at runtime for SPA pages
function upsertMeta(selector, attrs = {}) {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    Object.keys(attrs).forEach(k => el.setAttribute(k, attrs[k]));
    document.head.appendChild(el);
    return el;
  }
  Object.keys(attrs).forEach(k => el.setAttribute(k, attrs[k]));
  return el;
}

function sanitizeKeywordList(rawKeywords) {
  const blockedPatterns = [
    /开挂|透视|辅助挂|外挂|专用版/i,
    /微信|wx|qq|联系方式|引流|私域/i,
  ];

  const items = String(rawKeywords || '')
    .split(',')
    .map((k) => k.trim())
    .filter(Boolean);

  const deduped = [];
  const seen = new Set();
  for (const item of items) {
    const normalized = item.toLowerCase();
    if (seen.has(normalized)) continue;
    if (blockedPatterns.some((p) => p.test(item))) continue;
    seen.add(normalized);
    deduped.push(item);
  }

  return deduped;
}

export function setMeta({ title, description, keywords, url, image, type = 'website', robots = 'index,follow', jsonLd }) {
  if (title) document.title = title;

  const canonicalHostFromEnv = process.env.REACT_APP_CANONICAL_DOMAIN || process.env.REACT_APP_WEBSITE_DOMAIN;
  const currentHost = window.location.host;
  const currentProtocol = window.location.protocol.replace(':', '');
  const canonicalProtocol = process.env.REACT_APP_WEBSITE_PROTOCOL || currentProtocol;
  const isLocalHost = /localhost|127\.0\.0\.1/.test(currentHost);
  const canonicalHost = canonicalHostFromEnv || (isLocalHost ? '' : currentHost);

  const normalizePathname = (pathname) => {
    if (!pathname || pathname === '/') return '/';
    const hasFileExtension = /\/[^/]+\.[a-z0-9]+$/i.test(pathname);
    if (hasFileExtension || pathname.endsWith('/')) return pathname;
    return `${pathname}/`;
  };

  const buildAbsoluteUrl = (protocol, host, pathname, search = '', hash = '') => {
    const finalPathname = normalizePathname(pathname);
    return `${protocol}://${host}${finalPathname}${search}${hash}`;
  };

  const normalizeUrl = (rawUrl) => {
    if (!rawUrl) return rawUrl;
    try {
      const u = new URL(rawUrl, window.location.origin);
      const isRouteLikePath = !/\/[^/]+\.[a-z0-9]+$/i.test(u.pathname);
      const isCurrentOrCanonicalHost = (
        u.host === currentHost ||
        (canonicalHost && u.host === canonicalHost) ||
        /localhost|127\.0\.0\.1/.test(u.host)
      );

      if (/localhost|127\.0\.0\.1/.test(u.host)) {
        if (canonicalHost) {
          return buildAbsoluteUrl(canonicalProtocol, canonicalHost, u.pathname, u.search, u.hash);
        }
        return buildAbsoluteUrl(currentProtocol, currentHost, u.pathname, u.search, u.hash);
      }

      if (isRouteLikePath && isCurrentOrCanonicalHost) {
        return buildAbsoluteUrl(u.protocol.replace(':', ''), u.host, u.pathname, u.search, u.hash);
      }

      return rawUrl;
    } catch (e) {
      return rawUrl;
    }
  };

  const normalizeJsonLdUrls = (value) => {
    if (!value || typeof value !== 'object') return value;
    if (Array.isArray(value)) return value.map((item) => normalizeJsonLdUrls(item));

    const jsonLdUrlKeyPattern = /^(url|image|contentUrl|thumbnailUrl|downloadUrl|logo|item|sameAs|mainEntityOfPage)$/i;

    const next = {};
    Object.keys(value).forEach((key) => {
      const v = value[key];
      if (typeof v === 'string') {
        const isLocalAbsolute = /^https?:\/\/(localhost|127\.0\.0\.1)/i.test(v);
        if (jsonLdUrlKeyPattern.test(key) || isLocalAbsolute) {
          next[key] = normalizeUrl(v);
          return;
        }
      }

      if (v && typeof v === 'object') {
        next[key] = normalizeJsonLdUrls(v);
      } else {
        next[key] = v;
      }
    });
    return next;
  };

  // 默认附加一些品牌词，如果已有则不重复添加
  const brandKeywords = ['HHpoker', '德扑圈APP下载', '德州扑克规则', 'HHPOKER德州官网'];
  const pageKeywords = sanitizeKeywordList(keywords);
  const merged = [...pageKeywords];
  const lowerSet = new Set(merged.map((k) => k.toLowerCase()));
  for (const bk of brandKeywords) {
    if (!lowerSet.has(bk.toLowerCase())) {
      merged.push(bk);
    }
  }
  const finalKeywords = merged.join(', ');

  if (description) {
    upsertMeta('meta[name="description"]', { name: 'description', content: description });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: description });
  }

  if (robots) {
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
    upsertMeta('meta[name="googlebot"]', { name: 'googlebot', content: robots });
    upsertMeta('meta[name="baiduspider"]', { name: 'baiduspider', content: robots });
  }

  // Use finalKeywords instead of keywords
  if (finalKeywords) upsertMeta('meta[name="keywords"]', { name: 'keywords', content: finalKeywords });

  if (title) {
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: title });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: title });
  }

  if (url) {
    const finalUrl = normalizeUrl(url);
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: finalUrl });
    // canonical
    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', finalUrl);
  } else {
    const fallbackProtocol = canonicalHostFromEnv ? canonicalProtocol : currentProtocol;
    const fallbackHost = canonicalHost || currentHost;
    const canonicalUrl = buildAbsoluteUrl(
      fallbackProtocol,
      fallbackHost,
      window.location.pathname,
      window.location.search,
    );

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }

  if (image) {
    const finalImage = normalizeUrl(image);
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: finalImage });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: finalImage });
  }

  upsertMeta('meta[property="og:type"]', { property: 'og:type', content: type });
  // Ensure viewport is mobile-friendly and prevents zooming which can be annoying on app-like sites, 
  // but for accessibility user-scalable=yes is better. However, for "app-like" feel often no is used.
  // Matching index.html settings:
  upsertMeta('meta[name="viewport"]', { name: 'viewport', content: 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no' });

  // Baidu mobile specific
  upsertMeta('meta[name="applicable-device"]', { name: 'applicable-device', content: 'pc,mobile' });
  upsertMeta('meta[http-equiv="Cache-Control"][content="no-transform"]', { 'http-equiv': 'Cache-Control', content: 'no-transform' });
  upsertMeta('meta[http-equiv="Cache-Control"][content="no-siteapp"]', { 'http-equiv': 'Cache-Control', content: 'no-siteapp' });

  // structured data
  if (jsonLd) {
    let script = document.getElementById('json-ld');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'json-ld';
      document.head.appendChild(script);
    }
    try {
      script.text = JSON.stringify(normalizeJsonLdUrls(jsonLd));
    } catch (e) {
      script.text = String(jsonLd);
    }
  }
}

export const defaultSiteTitle = 'HHpoker官方网站_德扑圈APP下载_德州扑克攻略';
